import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, FileText, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/abi/public/ricevuta`;
const err = (e) => e.response?.data?.detail || "Si è verificato un errore";

export default function RicevutaDocumenti() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [errore, setErrore] = useState("");
  const [otpSent, setOtpSent] = useState(null);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => axios.get(`${API}/${token}`).then(({ data }) => setD(data)).catch((e) => setErrore(err(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const richiedi = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await axios.post(`${API}/${token}/request-otp`); setOtpSent(data.destinazione); }
    catch (e) { setMsg(err(e)); } finally { setBusy(false); }
  };
  const conferma = async () => {
    setBusy(true); setMsg("");
    try { await axios.post(`${API}/${token}/conferma`, { otp }); await load(); }
    catch (e) { setMsg(err(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-start justify-center px-4 py-12">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="ricevuta-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-teal-100 text-teal-700 flex items-center justify-center"><ShieldCheck className="h-5 w-5" /></div>
          <div><p className="text-xs uppercase tracking-[0.18em] text-teal-600">Assietica</p><h1 className="font-heading text-xl font-semibold text-slate-900">Conferma di ricezione documenti</h1></div>
        </div>
        {errore ? <p className="text-sm text-red-600" data-testid="ricevuta-errore">{errore}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : (
          <>
            <p className="text-sm text-slate-600 mb-4">Documenti inviati a <b>{d.ragione_sociale}</b> il {new Date(d.inviato_at).toLocaleDateString("it-IT")}. Puoi aprirli e scaricarli qui sotto.</p>
            <ul className="space-y-2 mb-6">
              {d.documenti.map((x, i) => (
                <li key={i}><a href={`${API}/${token}/doc/${i}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors" data-testid={`ricevuta-doc-${i}`}><FileText className="h-4 w-4 text-teal-600" />{x.label}</a></li>
              ))}
            </ul>
            {d.confermata ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="ricevuta-confermata">
                <CheckCircle2 className="h-5 w-5" /> Ricezione confermata il {new Date(d.confermata_at).toLocaleString("it-IT")}. Grazie.
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 p-4 space-y-3">
                <p className="text-sm text-slate-700">Per confermare di aver ricevuto i documenti, richiedi un codice: lo riceverai via SMS al numero {d.destinazione}.</p>
                {!otpSent ? (
                  <Button onClick={richiedi} disabled={busy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="ricevuta-richiedi-otp">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Invia il codice via SMS</Button>
                ) : (
                  <div className="flex gap-2">
                    <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Codice a 6 cifre" maxLength={6} inputMode="numeric" data-testid="ricevuta-otp-input" />
                    <Button onClick={conferma} disabled={busy || otp.length < 6} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="ricevuta-conferma">{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Conferma</Button>
                  </div>
                )}
                {otpSent && <button onClick={richiedi} disabled={busy} className="text-xs text-teal-700 hover:underline" data-testid="ricevuta-reinvia">Non hai ricevuto il codice? Invialo di nuovo</button>}
                {msg && <p className="text-sm text-red-600" data-testid="ricevuta-msg">{msg}</p>}
                <p className="text-[11px] text-slate-400">Confermando dichiari di aver ricevuto i documenti elencati. Verranno registrati data, ora e un'impronta dei documenti.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
